"use client";

import { useState } from "react";
import { runTranslator, AiToolError } from "@/lib/aiTools";
import { inputClass, OutputBox, ErrorBox, FreeTrialNote } from "@/components/aiTools/ToolUI";
import UpgradeToPro from "@/components/platform/UpgradeToPro";
import Spinner from "@/components/Spinner";

const LANGUAGES = ["Spanish", "French", "German", "Hindi", "Portuguese", "Japanese", "Mandarin Chinese", "Arabic", "Russian", "Italian"];

export default function TranslatorTool() {
  const [input, setInput] = useState("");
  const [target, setTarget] = useState("Spanish");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capped, setCapped] = useState(false);

  async function generate() {
    if (!input.trim()) return;
    setLoading(true);
    setError(null);
    setCapped(false);
    try {
      setOutput(await runTranslator(input.trim(), target));
    } catch (e) {
      if (e instanceof AiToolError && e.status === 429) {
        setCapped(true);
        setError(e.message);
      } else {
        setError(e instanceof AiToolError ? e.message : "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Text to translate</label>
      <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={5} placeholder="Type or paste text…" className={inputClass} />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Translate to:</label>
        <select value={target} onChange={(e) => setTarget(e.target.value)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200">
          {LANGUAGES.map((lang) => (
            <option key={lang} value={lang}>
              {lang}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={generate}
          disabled={loading || !input.trim()}
          className="ml-auto inline-flex items-center gap-2 rounded-full bg-violet-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-50"
        >
          {loading ? <Spinner className="h-4 w-4" /> : null}
          {loading ? "Translating…" : "Translate"}
        </button>
      </div>
      <FreeTrialNote />
      {error ? <ErrorBox message={error} /> : null}
      {capped ? (
        <div className="mt-3">
          <UpgradeToPro />
        </div>
      ) : null}
      {output ? <OutputBox output={output} /> : null}
    </div>
  );
}
